/**
 * Login.jsx — Page de connexion
 *
 * Permet à un utilisateur (client, agence, admin) de se connecter.
 * Après connexion réussie, les données du compte sont sauvegardées dans
 * localStorage et l'utilisateur est redirigé selon son rôle.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdEmail, MdLock } from 'react-icons/md';
import { FiArrowRight, FiEye, FiEyeOff, FiShield, FiLock, FiHeadphones, FiZap } from 'react-icons/fi';
import Navbar from '../shared/Navbar.jsx';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import '../home/Home.css';
import './Login.css';

const apiBaseUrl = getApiBaseUrl();

function Login() {
	const navigate = useNavigate();

	// ── État du formulaire ────────────────────────────────────────────────────
	const [identifiant, setIdentifiant] = useState('');
	const [motDePasse, setMotDePasse] = useState('');
	const [resterConnecte, setResterConnecte] = useState(false);
	const [chargement, setChargement] = useState(false);
	const [erreur, setErreur] = useState('');
	const [afficherMdp, setAfficherMdp] = useState(false);

	/**
	 * handleSoumission — Envoie les identifiants au backend.
	 * Si la réponse est OK, sauvegarde l'utilisateur en localStorage
	 * et redirige selon son rôle (admin → /admin, agence → /agence, user → /).
	 * Si le compte n'est pas vérifié, redirige vers /verify-email.
	 */
	const handleSoumission = async (e) => {
		e.preventDefault();
		setChargement(true);
		setErreur('');

		try {
			const res = await fetch(`${apiBaseUrl}/api/users/login`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ identifier: identifiant, password: motDePasse }),
			});

			// Vérifie que la réponse est bien du JSON avant de la parser
			const typeContenu = res.headers.get('content-type') || '';
			const data = typeContenu.includes('application/json')
				? await res.json()
				: { success: false, message: 'Réponse invalide du serveur.' };

			if (!res.ok || !data.success) {
				// Cas particulier : email pas encore vérifié
				if (data.requiresVerification && data.email) {
					navigate(`/verify-email?email=${encodeURIComponent(data.email)}`);
					return;
				}
				setErreur(data.message || 'Échec de connexion.');
				return;
			}

			// Sauvegarde du profil dans localStorage (lu par Navbar, BottomNav, etc.)
			window.localStorage.setItem('exp-auth-user', JSON.stringify(data.user));
			window.localStorage.setItem('exp-user-profile', JSON.stringify(data.user));

			// Option "Rester connecté" : mémorise le nom d'utilisateur
			if (resterConnecte) {
				window.localStorage.setItem('exp-remembered-username', identifiant);
			} else {
				window.localStorage.removeItem('exp-remembered-username');
			}

			// Redirection selon le rôle
			if (data.user.role === 'admin') navigate('/admin');
			else if (data.user.role === 'agency') navigate('/agence');
			else navigate('/');
		} catch {
			setErreur('Impossible de contacter le serveur.');
		} finally {
			setChargement(false);
		}
	};

	return (
		<div className="vx-login-page">
			<Navbar minimal />

			<main className="vx-login-main">

				{/* ── Panneau gauche : accroche marketing ── */}
				<div className="vx-login-left">
					<div className="vx-login-left-inner">
						<span className="vx-login-kicker">
							<FiZap size={13} aria-hidden="true" />
							Espace membre
						</span>
						<h1 className="vx-login-title">
							Bon retour,<br /><em>conducteur.</em>
						</h1>
						<p className="vx-login-subtitle">
							Connectez-vous pour accéder à vos réservations, préférences et recommandations IA.
						</p>
						<div className="vx-login-trust">
							<div className="vx-login-trust-item">
								<FiLock size={14} aria-hidden="true" /> Paiement sécurisé
							</div>
							<div className="vx-login-trust-item">
								<FiShield size={14} aria-hidden="true" /> Données protégées
							</div>
							<div className="vx-login-trust-item">
								<FiHeadphones size={14} aria-hidden="true" /> Support 24h/24 et 7J/7
							</div>
						</div>
					</div>
				</div>

				{/* ── Panneau droit : formulaire de connexion ── */}
				<div className="vx-login-right">
					<div className="vx-login-right-inner">

						<div className="vx-login-form-head">
							<span className="vx-section-kicker">Connexion</span>
							<h2 className="vx-login-form-title">Accédez à votre espace</h2>
							<p className="vx-login-form-sub">Entrez vos identifiants pour vous connecter.</p>
						</div>

						<form className="vx-login-card" onSubmit={handleSoumission} noValidate>

							{/* Champ e-mail */}
							<div className="vx-login-field">
								<label className="vx-login-label" htmlFor="vx-login-email">
									<MdEmail size={13} /> Adresse e-mail
								</label>
								<input
									id="vx-login-email"
									type="text"
									className="vx-login-input"
									placeholder="vous@ultimatexdrive.com"
									value={identifiant}
									onChange={(e) => setIdentifiant(e.target.value)}
									autoComplete="username"
									required
								/>
							</div>

							{/* Champ mot de passe avec bascule afficher/masquer */}
							<div className="vx-login-field">
								<label className="vx-login-label" htmlFor="vx-login-password">
									<MdLock size={13} /> Mot de passe
								</label>
								<div className="vx-login-pw-wrap">
									<input
										id="vx-login-password"
										type={afficherMdp ? 'text' : 'password'}
										className="vx-login-input"
										placeholder="Votre mot de passe"
										value={motDePasse}
										onChange={(e) => setMotDePasse(e.target.value)}
										autoComplete="current-password"
										required
									/>
									<button
										type="button"
										className="vx-pw-toggle"
										onClick={() => setAfficherMdp((v) => !v)}
										aria-label={afficherMdp ? 'Masquer' : 'Afficher'}
									>
										{afficherMdp ? <FiEyeOff size={16} /> : <FiEye size={16} />}
									</button>
								</div>
							</div>

							<div className="vx-login-options">
								<label className="vx-login-remember">
									<input
										type="checkbox"
										checked={resterConnecte}
										onChange={(e) => setResterConnecte(e.target.checked)}
									/>
									Rester connecté
								</label>
								<Link to="/reset-password" className="vx-login-forgot">
									Mot de passe oublié ?
								</Link>
							</div>

							{/* Message d'erreur accessible (role="alert" annoncé par les lecteurs d'écran) */}
							{erreur && <p className="vx-login-error" role="alert">⚠ {erreur}</p>}

							<button
								type="submit"
								className="vx-btn vx-btn-solid vx-login-submit"
								disabled={chargement}
							>
								{chargement ? (
									<span className="vx-login-loading">
										<span className="vx-login-dot" />
										<span className="vx-login-dot" />
										<span className="vx-login-dot" />
										Connexion en cours…
									</span>
								) : (
									<>Se connecter <FiArrowRight size={14} aria-hidden="true" /></>
								)}
							</button>

							<p className="vx-login-signup-row">
								Pas encore de compte ?{' '}
								<Link to="/signup">S&apos;inscrire maintenant</Link>
							</p>
						</form>
					</div>
				</div>
			</main>
		</div>
	);
}

export default Login;
